import { router } from 'expo-router';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { View } from 'react-native';

import { FadeView, MqSwitch, createMqStyles } from '@fhs/ui';
import { selectors } from '@fhs-legacy/frontend/src/state/global-state';
import { useAppSelector } from '@fhs-legacy/frontend/src/state/global-state';
import { routes } from '@fhs-legacy/frontend/src/utils/routing';

import {
  RewardTierCard,
  RewardTierCardSkeleton,
  VerticalTieredProgressBar,
} from '../../components';
import { useLoyaltyIncentives } from '../../queries/loyalty.queries';

import { useIsLoyaltyPageLoading } from './hooks';

export function RewardTiers({ isRefreshing = false }: { isRefreshing?: boolean }) {
  const mqStyles = useMqStyles();
  const { data } = useLoyaltyIncentives();
  const isLoading = useIsLoyaltyPageLoading();
  const intl = useIntl();

  const hasReachedMaxRedemptionLimit = useAppSelector(
    selectors.loyalty.createHasReachedMaxRedemptionLimitSelector(intl)
  );

  const rewardsTiersFormatted = useMemo(() => {
    if (isLoading) {
      return Array.from({ length: 6 }).map((_, index) => ({
        id: `skeleton-${index.toString()}`,
        label: '-',
        content: (
          <FadeView>
            <RewardTierCardSkeleton style={mqStyles.rewardTierCard} />
          </FadeView>
        ),
        enabled: false,
        isFullWidth: false,
      }));
    }
    return (
      data?.rewardTiers?.map(tier => ({
        id: tier.id,
        label: tier.points.toLocaleString(),
        content: (
          <FadeView>
            <RewardTierCard
              style={mqStyles.rewardTierCard}
              title={tier.name}
              description={tier.message}
              image={tier.image}
              pointsBadgeLabel={tier.points.toLocaleString()}
              isRedeemable={tier.isRedeemable && !hasReachedMaxRedemptionLimit}
              isDisabled={isRefreshing}
              learnMoreHref={tier.learnMoreHref}
              isEligible={tier.isEligible}
              onPressRedeem={() => router.navigate(routes.offersV2Guide + `/${tier.id}`)}
            />
          </FadeView>
        ),
        enabled: tier.isRedeemable,
        isFullWidth: typeof tier.isEligible === 'boolean' || !!tier.learnMoreHref,
      })) ?? []
    );
  }, [
    isLoading,
    data?.rewardTiers,
    mqStyles.rewardTierCard,
    hasReachedMaxRedemptionLimit,
    isRefreshing,
  ]);

  return (
    <View style={mqStyles.container}>
      <MqSwitch
        $base={() => (
          <VerticalTieredProgressBar
            key={'vertical-tiered-progress-bar' + isLoading}
            tiers={rewardsTiersFormatted}
          />
        )}
        $gteDesktop={() => (
          <View style={mqStyles.rewardTiersContainer}>
            {rewardsTiersFormatted.map(item => {
              const isFullWidth = item.isFullWidth;
              return (
                <View key={item.id} style={[isFullWidth ? { width: '100%' } : { width: '49.1%' }]}>
                  {item.content}
                </View>
              );
            })}
          </View>
        )}
      />
    </View>
  );
}

const useMqStyles = createMqStyles({
  container: {
    $base: {
      paddingHorizontal: 16,
      marginTop: 24,
      width: '100%',
    },
    $gteDesktop: {
      paddingHorizontal: 0,
      marginTop: 32,
    },
  },
  rewardTierCard: {
    $gteDesktop: {
      flex: 1,
    },
  },
  rewardTiersContainer: {
    $gteDesktop: {
      rowGap: 16,
      flexWrap: 'wrap',
      flexDirection: 'row',
      justifyContent: 'space-between',
    },
  },
});
